<template>
	<div class="filter-price">
		<div class="filter-toolbar">
			<span class="title">{{ $t('WEBAPP_PRICE') }}</span>
			<ResetButton :filterNames="['min_price', 'max_price']" />
		</div>
		<div class="jw-padding">
			<RangeInput class="range" v-model="price" :min="minPrice" :max="maxPrice" :step="priceStep">
				<template #start>{{ formattedMinPrice }}</template>
				<template #end>{{ formattedMaxPrice }}</template>
			</RangeInput>

			<div style="display: flex; flex-flow: wrap">
				<ToggleButton
					v-for="preset in presets"
					:key="preset.trackingKey"
					:active="isPriceFilterActive(preset.min_price, preset.max_price)"
					:icon="'jw-check-icon'"
					@click.prevent="setPreset(preset)"
				>
					{{ preset.label }}
				</ToggleButton>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import RangeInput from '@/ui-components/RangeInput.vue';
import ToggleButton from '@/ui-components/ToggleButton.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { WebLocale } from '@/enums/web-locale';
import { AppDirection } from '@/enums/app-direction';
import { CollectionType } from '@/enums/collection-type';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';

const props = defineProps<{
	collectionType: CollectionType;
	currentFilters: FilterCollection;
}>();

const { t } = useI18n();
const router = useRouter();

const languageStore = useLanguageStore();
const filterStore = useFilterStore();
const metaStore = useMetaStore();

interface PricePreset {
	min_price: number;
	max_price: number | null;
	label: string | any;
	trackingKey: string;
}

const FILTER_NAMES = ['min_price', 'max_price'] as const;
const PRICE_STEP = 1;
const BASE_MAX_PRICE = 50;
const PRESET_VALUES = [1, 2, 3, 5] as const;

const CURRENCY_AMPLIFIERS: Record<string, number> = {
	[WebLocale.br]: 5,
	[WebLocale.in]: 50,
	[WebLocale.jp]: 100,
	[WebLocale.kr]: 1000,
};

const currencyAmplifier = computed(() => CURRENCY_AMPLIFIERS[languageStore.webLocale] ?? 1);

const minPrice = 0;
const maxPrice = computed(() => currencyAmplifier.value * BASE_MAX_PRICE);
const priceStep = PRICE_STEP;

const currentPriceFilter = computed(() => {
	const collectionFilters = filterStore.filters(props.collectionType);
	return {
		min_price: collectionFilters.min_price,
		max_price: collectionFilters.max_price,
	};
});

const price = ref([props.currentFilters.min_price || minPrice, props.currentFilters.max_price || maxPrice.value]);

const presets = computed<PricePreset[]>(() => {
	const amp = currencyAmplifier.value;

	const result: PricePreset[] = PRESET_VALUES.map((multiplier): PricePreset => {
		const value = amp * multiplier;
		return {
			min_price: 0,
			max_price: value as number | null,
			label: t('WEBAPP_PRICEFILTER_UPTO', { x: value }) as string,
			trackingKey: `upto-${value}`,
		};
	});

	const lastValue = amp * 5;
	result.push({
		min_price: lastValue,
		max_price: null as number | null,
		label: t('WEBAPP_PRICEFILTER_MORETHAN', { x: lastValue }) as string,
		trackingKey: `morethan-${lastValue}`,
	});

	return result;
});

const formattedMinPrice = computed(() => formatPrice(minPrice));
const formattedMaxPrice = computed(() => formatPrice(maxPrice.value));

function setPreset(preset: PricePreset) {
	const isActive = isPriceFilterActive(preset.min_price, preset.max_price);

	let newRoute;
	if (isActive) {
		newRoute = filterStore.resetFilters({ filterNames: FILTER_NAMES });
		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'price',
			property: preset.trackingKey,
			value: 0,
		});
	} else {
		newRoute = filterStore.setRange({
			rangeName: 'price',
			trackingKey: preset.trackingKey,
			upper: preset.max_price,
			lower: preset.min_price,
			selectedState: 1,
		});
	}

	if (newRoute) router.push(newRoute);
}

function isPriceFilterActive(min: number, max: number | null): boolean {
	const { min_price, max_price } = currentPriceFilter.value;
	return min === min_price && max === max_price;
}

function formatPrice(priceValue: number): string {
	const currency = t('WEBAPP_STANDARD_CURRENCY');
	return metaStore.appDirection === AppDirection.RTL ? `${priceValue}${currency}` : `${currency}${priceValue}`;
}

// Track if we're updating from the store to prevent circular updates
let isUpdatingFromStore = false;

// Watch store changes and update price slider
watch(
	currentPriceFilter,
	({ min_price, max_price }) => {
		const newMin = min_price ?? minPrice;
		const newMax = max_price ?? maxPrice.value;

		if (newMin !== price.value[0] || newMax !== price.value[1]) {
			isUpdatingFromStore = true;
			price.value = [newMin, newMax];
		}
	},
	{ immediate: true }
);

// Watch price slider changes and update store
watch(price, newPrice => {
	// Skip if update came from store
	if (isUpdatingFromStore) {
		isUpdatingFromStore = false;
		return;
	}

	const [lower, upper] = newPrice;
	const max = maxPrice.value;

	const newRoute = filterStore.setRange({
		rangeName: 'price',
		trackingKey: 'knob',
		upper: upper === max ? null : upper,
		lower: lower === minPrice ? null : lower,
	});

	if (newRoute) router.push(newRoute);
});
</script>

<style lang="scss"></style>
